import { Component, computed, DestroyRef, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { MfgExpDatesIcon } from '../../../../../../shared/ui/icons/product-card/mfg-exp-dates-icon/mfg-exp-dates-icon';
import { BusinessPriceIcon } from '../../../../../../shared/ui/icons/product-card/business-price-icon/business-price-icon';
import { CustomerPriceIcon } from '../../../../../../shared/ui/icons/product-card/customer-price-icon/customer-price-icon';
import { BatchNumberIcon } from '../../../../../../shared/ui/icons/product-card/batch-number-icon/batch-number-icon';
import { NormalizedNotPackageRow } from '../../../../../../pages/admin-dashboard/orders-management/view-order/view-order.model';
import { ItemQuantityStatus } from '../item-quantity-status/item-quantity-status';
import { ProductsFileMgt } from '../../../../../../services/product/file-management/products.file-mgt.service';
import { Loading } from '../../../../../../utils/loading/loading';
import { SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-order-product-item',
  standalone: true,
  imports: [MfgExpDatesIcon, BusinessPriceIcon, CustomerPriceIcon, BatchNumberIcon, ItemQuantityStatus, Loading],
  templateUrl: './order-product-item.html',
  styleUrl: './order-product-item.scss',
})
export class OrderProductItem {
  private fileMgt = inject(ProductsFileMgt);
  private destroyRef = inject(DestroyRef);
  private locale = inject(LOCALE_ID);

  isArabic = computed(() => this.locale.startsWith('ar'));
  productTitle = input();
  mfgDate = input();
  expDate = input();
  businessPrice = input();
  peoplePrice = input<string>();
  totalBusinessPrice = input<number>();
  quantity = input<string | number>('');
  batchNumber = input();
  isPrinted = input<boolean>(false);

  isReadOnly = input<boolean>(false);

  packageQuantity = input<string | null>(null);
  notPackageRows = input<NormalizedNotPackageRow[] | null>(null);

  packageClicked = output<void>();

  isPartiallyPackaged = input<boolean>(false);
  confirmAction = input<string | null>(null);

  showOnlyPackaged = input<boolean>(false);

  confirmShortageClicked = output<void>();
  dropShortageClicked = output<void>();

  displayPharmacyPrice = computed(() => {
    const total = +(this.totalBusinessPrice() || 0);
    if (!this.showOnlyPackaged()) return total;
    const qty = +(this.packageQuantity() || '0');
    const unit = parseFloat(String(this.businessPrice() ?? '0'));
    return +(qty * unit).toFixed(2);
  });

  totalPeoplePriceComputed = computed(() => {
    return +(this.peoplePrice() || 0) * +(this.quantity() || 0);
  });

  showShortageActions = computed(
    () =>
      !this.isReadOnly() &&
      this.isPartiallyPackaged() &&
      (this.notPackageRows()?.length ?? 0) > 0 &&
      !this.confirmAction(),
  );

  isFullyUnpackaged = computed(() => {
    const packed = this.packageQuantity();
    const hasPackaged = packed !== null && Number(packed) > 0;
    if (hasPackaged) return false;
    const rows = this.notPackageRows() ?? [];
    if (rows.length === 0) return false;
    const notPacked = rows.reduce((acc, r) => acc + (+r.quantity || 0), 0);
    return notPacked >= +this.quantity();
  });

  quantityString = computed(() => String(this.quantity()));
  profileImageFid = input<string | undefined | null>();
  profileImageUrl = input<string | SafeUrl>();
  isLoadingFile = signal(false);
  previewImageUrl = signal<SafeUrl | string>('/assets/product.png');
  isProductOffer = input();
  role = input<'admin' | 'pharmacist'>('admin');
  receiveReturnedProductsView = input<boolean>(false);

  mfgDateYear = computed(() => {
    const date = new Date(this.mfgDate() as any);
    const shortYear = date.getFullYear().toString().slice(-2);
    return shortYear;
  });

  expDateYear = computed(() => {
    const date = new Date(this.expDate() as any);
    const shortYear = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');

    return month + ' - ' + shortYear;
  });

  concatenationDate = computed(() => this.mfgDateYear() + ' - ' + this.expDateYear());

  ngOnChanges() {
    const url = this.profileImageUrl();
    if (url) {
      this.previewImageUrl.set(url);
      return;
    }

    const fid = this.profileImageFid();
    if (!fid) return;

    this.isLoadingFile.set(true);

    const subscription = this.fileMgt.getImageUrl(fid).subscribe({
      next: (res) => {
        this.isLoadingFile.set(false);

        if (res.isImage) {
          this.previewImageUrl.set(res.url);
        } else {
          window.open(res.url as string, '_blank');
        }
      },
      error: () => {
        this.isLoadingFile.set(false);
        this.previewImageUrl.set('/assets/product.png');
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onOpenPackageModal() {
    if (this.isReadOnly()) return;
    this.packageClicked.emit();
  }

  onConfirmShortage() {
    if (this.isReadOnly()) return;
    this.confirmShortageClicked.emit();
  }

  onDropShortage() {
    if (this.isReadOnly()) return;
    this.dropShortageClicked.emit();
  }
}
